import * as XLSX from 'xlsx';
import { Timestamp } from 'firebase/firestore';
import { fuelTransactionsService } from './fuel-transactions';
import { shipmentsService } from './shipments';
import { vehicleMappingService } from './vehicle-mapping';
import { odometerService } from './odometer';
import { odometerAutomationService } from './odometer-automation';
import { vehiclesService } from './vehicles';
import { licensePlatesMatch, normalizeLicensePlate, generatePlateVariations } from '../../utils/arabic-english-converter';

class EnhancedFuelImportService {
  constructor() {
    this.supportedFormats = ['.xlsx', '.xls'];
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
    this.batchSize = 500;
  }

  // Enhanced auto-matching with better debugging
  async autoMatchTransactions(companyId, transactions) {
    try {
      // Get all shipments for the company
      const shipments = await shipmentsService.getShipments(companyId);

      console.log('🚛 Total shipments loaded:', shipments.length);
      console.log('⛽ Total transactions to match:', transactions.length);

      // Filter shipments that have vehicle plates
      const shipmentsWithVehicles = shipments.filter(s => s.vehiclePlate);
      console.log('🚛 Shipments with vehicle plates:', shipmentsWithVehicles.length);

      const matchedTransactions = [];
      const unmatchedTransactions = [];

      for (const transaction of transactions) {
        console.log('\n🔄 Processing transaction:', transaction.invoice_number);
        
        // Initialize debugging info
        transaction.hasVehicleMatch = false;
        transaction.hasTimeMatch = false;
        transaction.potentialMatches = 0;
        transaction.unmatchedReason = null;
        transaction.nearestShipmentDays = null;
        
        const matchedShipment = this.findMatchingShipmentEnhanced(transaction, shipmentsWithVehicles);

        if (matchedShipment) {
          console.log('✅ Transaction matched to shipment:', matchedShipment.trackingNumber || matchedShipment.id);
          matchedTransactions.push({
            ...transaction,
            shipment_id: matchedShipment.id,
            matched: true,
            matchedShipment: matchedShipment
          });
        } else {
          console.log('❌ Transaction remains unmatched:', transaction.unmatchedReason);
          unmatchedTransactions.push(transaction);
        }
      }

      const matchRate = transactions.length > 0 ? matchedTransactions.length / transactions.length : 0;
      console.log(`📊 Final matching results: ${matchedTransactions.length}/${transactions.length} (${(matchRate * 100).toFixed(1)}%)`);

      return {
        matched: matchedTransactions,
        unmatched: unmatchedTransactions,
        matchRate
      };
    } catch (error) {
      console.error('Error auto-matching transactions:', error);
      throw error;
    }
  }

  // Enhanced matching with detailed debugging
  findMatchingShipmentEnhanced(transaction, shipments) {
    try {
      const transactionPlate = transaction.mappedVehiclePlate || transaction.vehicle_plate;
      console.log('🔍 Looking for shipment match for vehicle plate:', transactionPlate);

      if (!transactionPlate) {
        console.warn('⚠️ Transaction has no vehicle plate');
        transaction.unmatchedReason = 'لا يوجد رقم لوحة للمعاملة';
        return null;
      }

      // Step 1: Find vehicle matches
      const vehicleMatches = shipments.filter(shipment => {
        if (!shipment.vehiclePlate) return false;
        try {
          return licensePlatesMatch(transactionPlate, shipment.vehiclePlate);
        } catch (error) {
          console.warn('⚠️ Error matching plates:', error);
          return false;
        }
      });

      transaction.hasVehicleMatch = vehicleMatches.length > 0;
      transaction.potentialMatches = vehicleMatches.length;

      console.log('📊 Found', vehicleMatches.length, 'vehicle matches for', transactionPlate);

      if (vehicleMatches.length === 0) {
        // No vehicle matches found
        transaction.unmatchedReason = 'مركبة غير موجودة في النظام';
        
        // Try to find similar plates for better debugging
        try {
          const availablePlates = shipments
            .filter(s => s.vehiclePlate)
            .map(s => s.vehiclePlate);

          const similarPlates = availablePlates.filter(plate => {
            try {
              const similarity = this.calculatePlateSimilarity(transactionPlate, plate);
              return similarity > 0.5;
            } catch (error) {
              console.warn('⚠️ Error calculating plate similarity:', error);
              return false;
            }
          });

          if (similarPlates.length > 0) {
            transaction.unmatchedReason = `مركبة مشابهة موجودة (${similarPlates.length} مطابقات محتملة)`;
            console.log('🔍 Similar plates found:', similarPlates.slice(0, 3));
          }
        } catch (error) {
          console.warn('⚠️ Error finding similar plates:', error);
        }

        return null;
      }

      // Step 2: Time-based matching
      let transactionTime;
      try {
        if (transaction.fill_time?.toDate) {
          transactionTime = transaction.fill_time.toDate();
        } else if (transaction.fill_time) {
          transactionTime = new Date(transaction.fill_time);
        } else {
          console.warn('⚠️ Transaction has no fill_time');
          transaction.unmatchedReason = 'لا يوجد وقت تعبئة للمعاملة';
          return null;
        }

        if (isNaN(transactionTime.getTime())) {
          console.warn('⚠️ Invalid transaction fill_time');
          transaction.unmatchedReason = 'وقت تعبئة غير صالح';
          return null;
        }
      } catch (error) {
        console.error('❌ Error parsing transaction time:', error);
        transaction.unmatchedReason = 'خطأ في تحليل وقت المعاملة';
        return null;
      }

      const transactionDate = this.getDateOnly(transactionTime);

      // Find same-day shipments
      const sameDayShipments = vehicleMatches.filter(shipment => {
        try {
          const shipmentTime = this.getShipmentTime(shipment);
          if (!shipmentTime) return false;

          const shipmentDate = this.getDateOnly(shipmentTime);
          return this.isSameDay(transactionDate, shipmentDate);
        } catch (error) {
          console.warn('⚠️ Error checking same-day shipment:', error);
          return false;
        }
      });

      transaction.hasTimeMatch = sameDayShipments.length > 0;

      if (sameDayShipments.length > 0) {
        // Same-day match found
        try {
          const earliest = sameDayShipments.reduce((earliest, shipment) => {
            const shipmentTime = this.getShipmentTime(shipment);
            const earliestTime = this.getShipmentTime(earliest);
            return shipmentTime && earliestTime && shipmentTime < earliestTime ? shipment : earliest;
          });
          
          console.log('✅ Matched to same-day shipment:', earliest.trackingNumber || earliest.id);
          return earliest;
        } catch (error) {
          console.error('❌ Error finding earliest same-day shipment:', error);
        }
      }

      // Find shipments before fill time
      const shipmentsBeforeFill = vehicleMatches.filter(shipment => {
        try {
          const shipmentTime = this.getShipmentTime(shipment);
          return shipmentTime && shipmentTime < transactionTime;
        } catch (error) {
          console.warn('⚠️ Error checking shipment before fill:', error);
          return false;
        }
      });

      if (shipmentsBeforeFill.length > 0) {
        try {
          // Find most recent before fill
          const mostRecent = shipmentsBeforeFill.reduce((mostRecent, shipment) => {
            const shipmentTime = this.getShipmentTime(shipment);
            const mostRecentTime = this.getShipmentTime(mostRecent);
            return shipmentTime && mostRecentTime && shipmentTime > mostRecentTime ? shipment : mostRecent;
          });

          // Calculate days difference
          const mostRecentTime = this.getShipmentTime(mostRecent);
          if (mostRecentTime) {
            const daysDiff = Math.ceil((transactionTime - mostRecentTime) / (1000 * 60 * 60 * 24));
            transaction.nearestShipmentDays = daysDiff;

            console.log('✅ Matched to most recent before fill:', mostRecent.trackingNumber || mostRecent.id, `(${daysDiff} days ago)`);
            return mostRecent;
          }
        } catch (error) {
          console.error('❌ Error finding most recent shipment:', error);
        }
      }

      // Find earliest shipment (transaction before all shipments)
      if (vehicleMatches.length > 0) {
        try {
          const earliestShipment = vehicleMatches.reduce((earliest, shipment) => {
            const shipmentTime = this.getShipmentTime(shipment);
            const earliestTime = this.getShipmentTime(earliest);
            return shipmentTime && earliestTime && shipmentTime < earliestTime ? shipment : earliest;
          });

          // Calculate days difference
          const earliestTime = this.getShipmentTime(earliestShipment);
          if (earliestTime) {
            const daysDiff = Math.ceil((earliestTime - transactionTime) / (1000 * 60 * 60 * 24));
            transaction.nearestShipmentDays = daysDiff;

            console.log('✅ Matched to earliest shipment:', earliestShipment.trackingNumber || earliestShipment.id, `(${daysDiff} days later)`);
            return earliestShipment;
          }
        } catch (error) {
          console.error('❌ Error finding earliest shipment:', error);
        }
      }

      // No suitable match found
      transaction.unmatchedReason = 'لا توجد شحنات مطابقة للتوقيت';
      return null;

    } catch (error) {
      console.error('❌ Error in findMatchingShipmentEnhanced:', error);
      transaction.unmatchedReason = 'خطأ في البحث عن مطابقة';
      return null;
    }
  }

  // Helper methods
  getShipmentTime(shipment) {
    const dateFields = ['deliveryDate', 'pickupDate', 'createdAt', 'updatedAt'];

    for (const field of dateFields) {
      if (shipment[field]) {
        try {
          let date;
          const value = shipment[field];

          if (value.seconds) {
            date = new Date(value.seconds * 1000);
          } else if (value.toDate) {
            date = value.toDate();
          } else {
            date = new Date(value);
          }

          if (!isNaN(date.getTime())) {
            return date;
          }
        } catch (error) {
          console.warn(`⚠️ Error parsing ${field} for shipment ${shipment.trackingNumber || shipment.id}:`, error);
        }
      }
    }

    return null;
  }

  getDateOnly(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  isSameDay(date1, date2) {
    return date1.getTime() === date2.getTime();
  }

  calculatePlateSimilarity(plate1, plate2) {
    if (!plate1 || !plate2) return 0;

    const norm1 = normalizeLicensePlate(plate1);
    const norm2 = normalizeLicensePlate(plate2);

    if (norm1 === norm2) return 1.0;

    const maxLength = Math.max(norm1.length, norm2.length);
    if (maxLength === 0) return 0;

    let commonChars = 0;
    const minLength = Math.min(norm1.length, norm2.length);

    for (let i = 0; i < minLength; i++) {
      if (norm1[i] === norm2[i]) {
        commonChars++;
      }
    }

    return commonChars / maxLength;
  }
}

export const enhancedFuelImportService = new EnhancedFuelImportService();